import { create } from 'zustand';

import { getPage } from '@/services/daily/daily-bubble.service';
import {
  Enum_Completedtask_Type,
  InsightEntity,
  DailyBubblePage,
  QuoteEntity,
  MindfulMinuteEntity,
  MutationCreateCompletedTaskArgs,
} from '@/graphql/generated/graphql';

import { CompletedTask, completeTask, dailyTaskHasBeenCompleted } from '@/services/daily/complete-tasks.service';
import { getDailyInsight } from '@/services/daily/daily-insight.service';
import { getMindfulMinute } from '@/services/daily/mindful-minute.service';
import { getDailyQuote } from '@/services/daily/quote.service';

interface HomeStoreProps {
  // HomeStore states
  page: DailyBubblePage | null;
  loadingPage: boolean;
  quote: QuoteEntity | null;
  mindfulMinute: MindfulMinuteEntity | null;

  // DailyStore states
  dailyInsight: { insight: InsightEntity } | null;
  dailyInsightCompleted: boolean;
  loadingDailyInsight: boolean;
  dailyEmotionWheelCompleted: boolean;
  mindfulMinuteCompleted: boolean;
  dailyQuoteCompleted: boolean;

  // HomeStore methods
  fetchHomePage: () => Promise<void>;

  // DailyStore methods
  handleDailyQuote: () => Promise<void>;
  handleDailyInsight: () => Promise<void>;
  handleEmotionWheel: () => Promise<void>;
  handleMindfulMinute: () => Promise<void>;
  initDailyData: () => Promise<void>;
  handleCompleteTask: ({ entity, type }: CompletedTask) => Promise<{
    data: MutationCreateCompletedTaskArgs | null | undefined;
    error: string | undefined;
  }>;
}

export const useHomeStore = create<HomeStoreProps>((set, get) => ({
  // Initial states
  page: null,
  loadingPage: false,
  quote: null,
  mindfulMinute: null,

  dailyInsight: null,
  dailyInsightCompleted: false,
  loadingDailyInsight: false,
  dailyEmotionWheelCompleted: false,
  mindfulMinuteCompleted: false,
  dailyQuoteCompleted: false,

  // HomeStore methods
  fetchHomePage: async () => {
    set({ loadingPage: true });
    const page = await getPage();
    set({ page: page.page, loadingPage: false });
  },

  // Combined handleDailyQuote method
  handleDailyQuote: async () => {
    try {
      const quoteData = await getDailyQuote();
      const { data, error } = await dailyTaskHasBeenCompleted(quoteData?.id || null, Enum_Completedtask_Type.Quote);
      if (error) {
        console.error('Error checking if daily quote has been completed', error);
        return;
      }
      set({
        quote: quoteData,
        dailyQuoteCompleted: !!data?.isCompleted,
      });
    } catch (error) {
      console.error('Error fetching daily quote completion status', error);
    }
  },

  handleDailyInsight: async () => {
    set({ loadingDailyInsight: true });
    const insight = await getDailyInsight();
    if (insight) {
      const { data, error } = await dailyTaskHasBeenCompleted(insight.insight.id!, Enum_Completedtask_Type.Insight);
      if (error) {
        console.error('Error checking if daily insight has been completed', error);
        set({ loadingDailyInsight: false });
        return;
      }
      set({
        dailyInsightCompleted: data.isCompleted,
        dailyInsight: insight,
        loadingDailyInsight: false,
      });
    } else {
      set({ loadingDailyInsight: false });
    }
  },

  handleEmotionWheel: async () => {
    const { data, error } = await dailyTaskHasBeenCompleted(null, Enum_Completedtask_Type.EmotionalWheel);
    if (error) {
      console.error('Error checking if emotion wheel has been completed', error);
      return;
    }
    set({
      dailyEmotionWheelCompleted: data.isCompleted,
    });
  },

  handleMindfulMinute: async () => {
    const currentMindfulMinute = await getMindfulMinute();
    if (currentMindfulMinute?.id) {
      const { data } = await dailyTaskHasBeenCompleted(currentMindfulMinute.id, Enum_Completedtask_Type.MindfulMinute);
      set({
        mindfulMinute: currentMindfulMinute,
        mindfulMinuteCompleted: !!data?.isCompleted,
      });
    }
  },

  handleCompleteTask: async ({ entity, type }: CompletedTask) => {
    if (type === Enum_Completedtask_Type.Quote) {
      set({ dailyQuoteCompleted: true });
    }
    return completeTask({ entity, type });
  },

  initDailyData: async () => {
    await Promise.all([
      get().handleDailyInsight(),
      get().handleEmotionWheel(),
      get().handleMindfulMinute(),
      get().handleDailyQuote(),
    ]);
  },
}));
